import { useEffect, useState, type FormEvent } from 'react';
import { api, apiForm } from '../api/client';
import type {
  ActiveDrop,
  CreateFileResponse,
  CreateSecretResponse,
  CreateUploadLinkResponse,
  UploadLinkRow,
} from '../api/types';
import FileDropZone, { fmtBytes } from '../components/FileDropZone';

type Tab = 'secret' | 'file' | 'reverse';

export default function DashboardPage() {
  const [tab, setTab] = useState<Tab>('secret');
  const [drops, setDrops] = useState<ActiveDrop[]>([]);
  const [links, setLinks] = useState<UploadLinkRow[]>([]);
  const [copied, setCopied] = useState<string | null>(null);

  async function copyToClipboard(text: string, which: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      setTimeout(() => setCopied((c) => (c === which ? null : c)), 1500);
    } catch {
      // clipboard may be unavailable on http://; the .token div has
      // user-select:all as a fallback.
    }
  }

  async function loadDrops() {
    try {
      const r = await api<{ drops: ActiveDrop[] }>('/secrets/list');
      setDrops(r.drops);
    } catch {
      // best-effort
    }
  }
  async function loadLinks() {
    try {
      const r = await api<{ links: UploadLinkRow[] }>('/upload-links/list');
      setLinks(r.links);
    } catch {
      // best-effort
    }
  }
  function refresh() {
    void loadDrops();
    void loadLinks();
  }
  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 10000);
    return () => clearInterval(t);
  }, []);

  return (
    <>
      <div className="card">
        <div className="tabs" role="tablist" aria-label="drop type">
          <button
            role="tab"
            aria-selected={tab === 'secret'}
            className={tab === 'secret' ? 'active' : ''}
            onClick={() => setTab('secret')}
          >
            secret
          </button>
          <button
            role="tab"
            aria-selected={tab === 'file'}
            className={tab === 'file' ? 'active' : ''}
            onClick={() => setTab('file')}
          >
            file
          </button>
          <button
            role="tab"
            aria-selected={tab === 'reverse'}
            className={tab === 'reverse' ? 'active' : ''}
            onClick={() => setTab('reverse')}
          >
            reverse-drop
          </button>
        </div>

        {tab === 'secret' && (
          <SecretForm onCreated={refresh} copy={copyToClipboard} copied={copied} />
        )}
        {tab === 'file' && (
          <FileForm onCreated={refresh} copy={copyToClipboard} copied={copied} />
        )}
        {tab === 'reverse' && (
          <ReverseDropForm onCreated={refresh} copy={copyToClipboard} copied={copied} />
        )}
      </div>

      <ActiveDropsCard drops={drops} />
      {links.length > 0 && <UploadLinksCard links={links} />}
    </>
  );
}

type CopyFn = (text: string, which: string) => void;

// ── secret drop ─────────────────────────────────────────────────

function SecretForm({
  onCreated,
  copy,
  copied,
}: {
  onCreated: () => void;
  copy: CopyFn;
  copied: string | null;
}) {
  const [secret, setSecret] = useState('');
  const [label, setLabel] = useState('');
  const [ttl, setTtl] = useState('300');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState<CreateSecretResponse | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setCreated(null);
    setBusy(true);
    try {
      const ttlNum = parseInt(ttl, 10);
      const r = await api<CreateSecretResponse>('/secrets', {
        method: 'POST',
        body: JSON.stringify({
          secret,
          ttl_seconds: Number.isFinite(ttlNum) && ttlNum > 0 ? ttlNum : undefined,
          label: label || undefined,
        }),
      });
      setCreated(r);
      setSecret('');
      setLabel('');
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed');
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setSecret('');
    setLabel('');
    setTtl('300');
    setError('');
    setCreated(null);
  }

  return (
    <>
      <NewPill onClick={reset} disabled={busy} />
      <p className="muted" style={{ marginBottom: 16 }}>
        Submit a secret. We hand you back a single-use, url-safe token. Share the URL;
        the receiver retrieves it exactly once via <code>/r/&lt;token&gt;</code>. Token +
        secret are wiped after retrieval or expiry.
      </p>
      {error && <div className="error-banner">{error}</div>}
      {created && (
        <CreatedBanner
          url={retrieveUrl(created.token)}
          token={created.token}
          expiresAt={created.expires_at}
          meta={`ttl_seconds=${created.ttl_seconds} · token_hash=${created.token_hash.slice(0, 12)}…`}
          copy={copy}
          copied={copied}
        />
      )}
      <form onSubmit={submit}>
        <label className="label">Secret</label>
        <textarea
          value={secret}
          onChange={(e) => setSecret(e.target.value)}
          placeholder="paste the value here"
          required
        />
        <div style={{ height: 12 }} />
        <MetaFields label={label} setLabel={setLabel} ttl={ttl} setTtl={setTtl} />
        <div className="actions">
          <button type="submit" disabled={busy || !secret}>
            {busy ? 'creating…' : 'create drop'}
          </button>
        </div>
      </form>
    </>
  );
}

// ── file drop (#14) ─────────────────────────────────────────────

function FileForm({
  onCreated,
  copy,
  copied,
}: {
  onCreated: () => void;
  copy: CopyFn;
  copied: string | null;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [label, setLabel] = useState('');
  const [ttl, setTtl] = useState('900');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState<CreateFileResponse | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!file) return;
    setError('');
    setCreated(null);
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const ttlNum = parseInt(ttl, 10);
      if (Number.isFinite(ttlNum) && ttlNum > 0) form.append('ttl_seconds', String(ttlNum));
      if (label) form.append('label', label);
      const r = await apiForm<CreateFileResponse>('/files', form);
      setCreated(r);
      setFile(null);
      setLabel('');
      onCreated();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setFile(null);
    setLabel('');
    setTtl('900');
    setError('');
    setCreated(null);
  }

  return (
    <>
      <NewPill onClick={reset} disabled={busy} />
      <p className="muted" style={{ marginBottom: 16 }}>
        Upload a file. It is held <strong>in memory only</strong> and handed out exactly
        once via <code>/r/&lt;token&gt;</code> (downloaded as an attachment), then wiped.
        Nothing is persisted to disk.
      </p>
      {error && <div className="error-banner">{error}</div>}
      {created && (
        <CreatedBanner
          url={retrieveUrl(created.token)}
          token={created.token}
          expiresAt={created.expires_at}
          meta={`${created.filename} · ${fmtBytes(created.size)} · token_hash=${created.token_hash.slice(0, 12)}…`}
          copy={copy}
          copied={copied}
        />
      )}
      <form onSubmit={submit}>
        <FileDropZone file={file} onPick={setFile} disabled={busy} />
        <div style={{ height: 12 }} />
        <MetaFields label={label} setLabel={setLabel} ttl={ttl} setTtl={setTtl} />
        <div className="actions">
          <button type="submit" disabled={busy || !file}>
            {busy ? 'uploading…' : 'create file drop'}
          </button>
        </div>
      </form>
    </>
  );
}

// ── reverse-drop / upload link (#15) ────────────────────────────

function ReverseDropForm({
  onCreated,
  copy,
  copied,
}: {
  onCreated: () => void;
  copy: CopyFn;
  copied: string | null;
}) {
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState<CreateUploadLinkResponse | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setCreated(null);
    setBusy(true);
    try {
      const r = await api<CreateUploadLinkResponse>('/upload-links', {
        method: 'POST',
        body: JSON.stringify({ label: label || undefined }),
      });
      setCreated(r);
      setLabel('');
      onCreated();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setLabel('');
    setError('');
    setCreated(null);
  }

  return (
    <>
      <NewPill onClick={reset} disabled={busy} />
      <p className="muted" style={{ marginBottom: 16 }}>
        Generate a <strong>one-time, unauthenticated upload link</strong>. Hand the URL to
        an external party; they upload <strong>one file once</strong>. The file lands as an
        in-memory single-use drop you can retrieve from <em>active drops</em> below. The
        link burns on first use.
      </p>
      {error && <div className="error-banner">{error}</div>}
      {created && (
        <div className="success-banner" style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8 }}>
            upload link created — give this URL to the uploader (single-use, expires{' '}
            {fmtDate(created.expires_at)}):
          </div>
          <div className="token">{absUrl(created.url)}</div>
          <div className="copy-row">
            <button
              type="button"
              className="secondary"
              onClick={() => copy(absUrl(created.url), 'uplink')}
            >
              {copied === 'uplink' ? 'copied!' : 'copy upload URL'}
            </button>
          </div>
        </div>
      )}
      <form onSubmit={submit}>
        <label className="label">Label (optional)</label>
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="resume from candidate X"
          maxLength={200}
        />
        <div className="actions">
          <button type="submit" disabled={busy}>
            {busy ? 'creating…' : 'create upload link'}
          </button>
        </div>
      </form>
    </>
  );
}

// ── shared bits ─────────────────────────────────────────────────

/// "+ new" pill: resets the current tab's form so the operator can
/// create another drop right after the previous one, without a page
/// reload. Sits above each tab's helper text.
function NewPill({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  return (
    <div className="tab-toolbar">
      <button type="button" className="pill" onClick={onClick} disabled={disabled}>
        + new
      </button>
    </div>
  );
}

function MetaFields({
  label,
  setLabel,
  ttl,
  setTtl,
}: {
  label: string;
  setLabel: (v: string) => void;
  ttl: string;
  setTtl: (v: string) => void;
}) {
  return (
    <div className="row">
      <div>
        <label className="label">Label (optional)</label>
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="GITLAB_TOKEN for tta"
          maxLength={200}
        />
      </div>
      <div>
        <label className="label">TTL (seconds)</label>
        <input
          value={ttl}
          onChange={(e) => setTtl(e.target.value)}
          inputMode="numeric"
          pattern="[0-9]+"
        />
      </div>
    </div>
  );
}

function CreatedBanner({
  url,
  token,
  expiresAt,
  meta,
  copy,
  copied,
}: {
  url: string;
  token: string;
  expiresAt: number;
  meta: string;
  copy: CopyFn;
  copied: string | null;
}) {
  return (
    <div className="success-banner" style={{ marginBottom: 16 }}>
      <div style={{ marginBottom: 8 }}>
        drop created — give this URL to the recipient (single-use, expires{' '}
        {fmtDate(expiresAt)}):
      </div>
      <div className="token">{url}</div>
      <div className="copy-row">
        <button type="button" className="secondary" onClick={() => copy(url, 'url')}>
          {copied === 'url' ? 'copied!' : 'copy URL'}
        </button>
        <button type="button" className="secondary" onClick={() => copy(token, 'token')}>
          {copied === 'token' ? 'copied!' : 'copy token only'}
        </button>
      </div>
      <div style={{ marginTop: 12 }} className="muted">
        {meta}
      </div>
    </div>
  );
}

function ActiveDropsCard({ drops }: { drops: ActiveDrop[] }) {
  return (
    <div className="card">
      <h2>active drops</h2>
      {drops.length === 0 ? (
        <p className="muted">no active drops</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>token_hash</th>
                <th>label</th>
                <th className="hide-sm">created</th>
                <th className="hide-sm">expires</th>
                <th>remaining</th>
              </tr>
            </thead>
            <tbody>
              {drops.map((d) => (
                <tr key={d.token_hash}>
                  <td>
                    <code>{d.token_hash.slice(0, 12)}…</code>
                  </td>
                  <td>{d.label ?? '—'}</td>
                  <td className="muted hide-sm">{fmtDate(d.created_at)}</td>
                  <td className="muted hide-sm">{fmtDate(d.expires_at)}</td>
                  <td className="muted">{remaining(d.expires_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function UploadLinksCard({ links }: { links: UploadLinkRow[] }) {
  return (
    <div className="card">
      <h2>upload links</h2>
      <div style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th>label</th>
              <th>status</th>
              <th className="hide-sm">created</th>
              <th>expires</th>
            </tr>
          </thead>
          <tbody>
            {links.map((l) => (
              <tr key={l.token_hash}>
                <td>{l.label ?? '—'}</td>
                <td>
                  {l.result_token_hash ? (
                    <span style={{ color: 'var(--success)' }}>
                      used → {l.result_token_hash.slice(0, 12)}…
                    </span>
                  ) : (
                    <span className="muted">pending</span>
                  )}
                </td>
                <td className="muted hide-sm">{fmtDate(l.created_at)}</td>
                <td className="muted">{remaining(l.expires_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function errMsg(e: unknown): string {
  if (e instanceof Error) {
    if (/413/.test(e.message)) return 'file too large (exceeds the per-file size cap)';
    if (/507/.test(e.message)) return 'server is at its in-memory file budget — try later';
    return e.message;
  }
  return 'failed';
}

function retrieveUrl(token: string): string {
  return `${window.location.origin}/r/${token}`;
}
function absUrl(path: string): string {
  return `${window.location.origin}${path}`;
}
function fmtDate(ts: number): string {
  return new Date(ts * 1000).toLocaleString();
}
function remaining(ts: number): string {
  const s = Math.max(0, ts - Math.floor(Date.now() / 1000));
  if (s === 0) return 'expired';
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  return `${Math.floor(s / 3600)}h`;
}
